import type { UserContextEvent } from '../telemetry-models';

/**
 * User preferences information (language, timezone, accessibility preferences)
 */
export type PreferencesInfo = Pick<
  UserContextEvent,
  | 'language'
  | 'languages'
  | 'timezone'
  | 'direction'
  | 'color_scheme'
  | 'prefers_reduced_motion'
  | 'prefers_reduced_data'
  | 'prefers_contrast'
>;

/**
 * Detects user preferences for language, theme, and accessibility.
 */
export class PreferencesDetector {
  static detect(): PreferencesInfo {
    const language = navigator.language;
    const languages = navigator.languages ? Array.from(navigator.languages) : [language];

    return {
      language,
      languages,
      timezone: this._detectTimezone(),
      direction: this._detectDirection(),
      color_scheme: this._detectColorScheme(),
      prefers_reduced_motion: this._detectReducedMotion(),
      prefers_reduced_data: this._detectReducedData(),
      prefers_contrast: this._detectContrast(),
    };
  }

  private static _detectTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'Unknown';
    }
  }

  private static _detectDirection(): 'ltr' | 'rtl' | undefined {
    // Check document direction
    if (typeof document !== 'undefined') {
      const dir = document.dir || document.documentElement.dir;
      if (dir === 'rtl' || dir === 'ltr') {
        return dir as 'ltr' | 'rtl';
      }

      // Check computed style
      const computedDir = window.getComputedStyle(document.documentElement).direction;
      if (computedDir === 'rtl' || computedDir === 'ltr') {
        return computedDir as 'ltr' | 'rtl';
      }

      // Infer from language (RTL languages)
      const lang = navigator.language.toLowerCase();
      const rtlLanguages = ['ar', 'he', 'fa', 'ur', 'yi'];
      if (rtlLanguages.some((rtl) => lang.startsWith(rtl))) {
        return 'rtl';
      }
    }

    return 'ltr';
  }

  private static _detectColorScheme(): 'light' | 'dark' | 'no-preference' | undefined {
    if (!window.matchMedia) return undefined;

    if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light';
    return 'no-preference';
  }

  private static _detectReducedMotion(): boolean {
    return window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
  }

  private static _detectReducedData(): boolean {
    return window.matchMedia ? window.matchMedia('(prefers-reduced-data: reduce)').matches : false;
  }

  private static _detectContrast(): 'no-preference' | 'more' | 'less' | undefined {
    if (!window.matchMedia) return undefined;

    if (window.matchMedia('(prefers-contrast: more)').matches) return 'more';
    if (window.matchMedia('(prefers-contrast: less)').matches) return 'less';
    if (window.matchMedia('(prefers-contrast: no-preference)').matches) return 'no-preference';
    return undefined;
  }
}
